import {
  Badge,
  Divider, Flex,
  Heading, Icon,
  Spinner,
  Table, Tbody, Td, Th, Thead, Tr,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import { IoClose } from 'react-icons/io5'
import styled from 'styled-components'
import moment from 'moment'
import React, { useCallback, useContext, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import { UserContext } from '../../providers'
import { ActiveSessionResponse, DeleteSessionResponse } from '../../interfaces/ActiveSessionResponse'
import useToast from '../../hooks/useToast'
import PermissionGuard from '../PermissionGuard'
import { PermissionsEnum } from '../../../utils'
import { FaDesktop, FaMobileAlt, FaTabletAlt, FaLaptop } from 'react-icons/fa'
import { IconType } from 'react-icons'

const SessionTableWrapper = styled(Flex)`
gap: 1.5em;
padding-top: 1.5rem;
flex-direction: column;
`

export const SessionTable = (): React.ReactElement => {
  const { t } = useTranslation()

  const {
    user,
    loading: loadingUser,
    refetch: refetchUser,
  } = useContext(UserContext);

  const [data, setData] = useState<ActiveSessionResponse[]>()

  const { request } = useFetch<ActiveSessionResponse[]>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const toast = useToast()

  const getData = async (): Promise<void> => {
    if (user?.clientNumber) {
      const response = await request.get(
        `/api/user/${user?.id}/sessions`
      )

      if (request.error) {
        toast({
          description: t('error_fetching_data', 'Error in fetching data. Try refreshing the page.'),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        setData(response)
      }
    }
  }

  useEffect(() => {
    void getData()
  }, [user])

  const deviceIcon = (label: string, icon: IconType): React.ReactElement => (
    <Tooltip hasArrow label={label}>
      <span>
        <Icon
          as={icon}
          cursor="pointer"
          boxSize="1.2em"
          focusable="true"
          verticalAlign="middle"
          marginRight="4px"
        />
      </span>
    </Tooltip>
  )

  const iconType = (device: string): React.ReactElement => {
    switch (device) {
      case 'desktop':
        return deviceIcon(t('desktop', 'Desktop'), FaDesktop)
      case 'mobile':
        return deviceIcon(t('mobile', 'Mobile'), FaMobileAlt)
      case 'tablet':
        return deviceIcon(t('tablet', 'Tablet'), FaTabletAlt)
      default:
        return deviceIcon(t('unknown_device', 'Unknown device'), FaLaptop)
    }
  }


  /* 
    ==== TODO ====
    - Add loaders 
    - Refresh data after an action
    - Refactor hooks and handlers
    ==== TODO ====
  */

  // delete origin - DELETE '/api/users/:userId/sessions/:sessionId'
  const deleteSession = useCallback(async (userId, sessionId): Promise<boolean> => {
    try {
      if (sessionId) {
        const deleteSessionResponse = (
          await request.del(`/api/user/${userId}/sessions/${sessionId}`)
        ) as null | DeleteSessionResponse;

        if (request.error) {
          throw request.error;
        }

        toast({
          description: t(
            'session_delete_success_message',
            'Session terminated successfully.',
          ),
          status: 'success',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        if (deleteSessionResponse && deleteSessionResponse.reloadRequired) {
          location.reload();
        } else {
          getData();
        }
        return true
      }
      return true
    } catch (err) {
      console.log(err)
      toast({
        description: t(
          'something_went_wrong',
          'Something went wrong. Please try again.',
        ),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      return false
    }
  }, [])

  return (
    <SessionTableWrapper>
      <Heading
        as="h5"
        size="md"
        fontWeight="600"
        color={colors.appDarkIndigo}
      >
        {t('active_sessions', 'Active sessions')}
      </Heading>
      <Divider borderColor="appPaleBlue" />
      <Table>
        <Thead bgColor="appPaleGreyTwo">
          <Tr>
            <Th>{t('ip_address', 'IP address')}</Th>
            <Th>{t('location', 'Location')}</Th>
            <Th>{t('last_active', 'Last active')}</Th>
            <Th>{t('device', 'Device')}</Th>
            <Th>{t('platform', 'Platform')}</Th>
            <PermissionGuard
              permission={PermissionsEnum.SELF_USER_MANAGE}
            >
              {
                (hasPermission: boolean) => (
                  hasPermission ? (
                    <Th>{t('actions', 'Actions')}</Th>
                  ) : <></>
                )
              }
            </PermissionGuard>
          </Tr>
        </Thead>
        {<Tbody>
          {!request.loading && !request.error ? (
            <>
              {data && data.length ? (data.map((val, key) => {
             
                return (
                  <Tr key={key}>
                    <Td>{val.ipAddress}</Td>
                    <Td>{val.location}</Td>
                    {val.isCurrentSession ?
                      <Td>
                        <Badge colorScheme="green">{t('current_session', 'Current session')}</Badge>
                      </Td> :
                      <Td>
                        <Tooltip hasArrow label={moment(val.lastActiveOn).format('DD/MM/YYYY, h:mm:ss A').toLocaleString()}>
                          {moment(val.lastActiveOn).subtract(20, 's').fromNow()}
                        </Tooltip>
                      </Td>}
                    <Td>{iconType(val.device)} {val.os}</Td>
                    <Td>{iconType(val.platform)} {val.platform}</Td>
                    <PermissionGuard
                      permission={PermissionsEnum.SELF_USER_MANAGE}
                    >
                      {
                        (hasPermission: boolean) => (
                          hasPermission ? (
                            <Td>
                              <>
                                <Tooltip hasArrow label={t('session_end', 'End session')}>
                                  <span>
                                    <Icon
                                      as={IoClose}
                                      cursor="pointer"
                                      color="appRed"
                                      boxSize={6}
                                      borderRadius="full"
                                      focusable="true"
                                      verticalAlign="middle"
                                      onClick={() => deleteSession(user?.id, val?.id)}
                                    />
                                  </span>
                                </Tooltip>
                              </>
                            </Td>
                          ) : <></>
                        )
                      }
                    </PermissionGuard>
                  </Tr>
                )
              }
              )) : <Tr>
                <Td colSpan={5} textAlign="center">
                  <Text>{t('no_data_available', 'No data available')}</Text>
                </Td>
              </Tr>
              }
            </>)
            : <Tr>
              <Td colSpan={5} textAlign="center">
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="appPaleGrey"
                  color="appBlue"
                  size="xl"
                />
              </Td>
            </Tr>}
        </Tbody>}
      </Table>
    </SessionTableWrapper>
  )
}
